<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { ref } from 'vue';

import { UserService } from '../../api/user/userService';
import { RegisterZodSchema } from '../../pages/Schemas/RegisterZodValidationSchema';
import { validateSchema } from '../../pages/Schemas/Schemas';
import ButtonWithSpinner from '../UI/ButtonWithSpinner.vue';

const buttonState = ref<'pending' | 'success' | 'error'>('pending');
const registerError = ref<string | null>(null);
const isSubmitting = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const { handleSubmit: handleRegisterSubmit } = useForm({
  validationSchema: validateSchema(RegisterZodSchema),
  initialValues: {
    email: '',
    password: '',
    confirmPassword: '',
  },
});

const { value: registerEmail, errorMessage: registerEmailError } = useField('email');
const { value: registerPassword, errorMessage: registerPasswordError } = useField('password');
const { value: confirmPassword, errorMessage: confirmPasswordError } = useField('confirmPassword');

const onRegisterSubmit = handleRegisterSubmit(async (values) => {
  const { email, password } = values;
  buttonState.value = 'pending';
  isSubmitting.value = true;
  registerError.value = null;

  try {
    await UserService.register({ email, password });
    buttonState.value = 'success';
    emit('success');
  } catch (error) {
    buttonState.value = 'error';
    if (error instanceof Error) {
      if (error.message.includes('User with this email already exists')) {
        registerError.value = 'Konto z tym adresem email już istnieje';
      } else {
        registerError.value = 'Nie udało się zarejestrować. Spróbuj ponownie';
      }
    }
  } finally {
    isSubmitting.value = false;
  }
});

const emit = defineEmits<{
  (e: 'toggleForm'): void;
  (e: 'success'): void;
}>();
</script>

<template>
  <form
    class="register-form"
    @submit="onRegisterSubmit"
  >
    <div class="register-form__group">
      <label
        for="register-email"
        class="register-form__label"
        >Email</label
      >
      <input
        id="register-email"
        v-model="registerEmail"
        class="register-form__input"
        :class="{ 'register-form__input--error': registerEmailError }"
        type="email"
        placeholder="Wprowadź adres email"
      />
      <div
        v-if="registerEmailError"
        class="register-form__message"
      >
        <small class="register-form__error">{{ registerEmailError }}</small>
      </div>
    </div>

    <div class="register-form__group">
      <label
        for="register-password"
        class="register-form__label"
        >Hasło</label
      >
      <div class="register-form__input-wrapper">
        <input
          id="register-password"
          v-model="registerPassword"
          :type="showPassword ? 'text' : 'password'"
          class="register-form__input"
          :class="{ 'register-form__input--error': registerPasswordError }"
          placeholder="Wprowadź hasło"
        />
        <button
          type="button"
          class="register-form__toggle-password"
          @click="showPassword = !showPassword"
        >
          <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
        </button>
      </div>
      <div
        v-if="registerPasswordError"
        class="register-form__message"
      >
        <small class="register-form__error">{{ registerPasswordError }}</small>
      </div>
    </div>

    <div class="register-form__group">
      <label
        for="confirm-password"
        class="register-form__label"
        >Potwierdź hasło</label
      >
      <div class="register-form__input-wrapper">
        <input
          id="confirm-password"
          v-model="confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          class="register-form__input"
          :class="{ 'register-form__input--error': confirmPasswordError }"
          placeholder="Potwierdź hasło"
        />
        <button
          type="button"
          class="register-form__toggle-password"
          @click="showConfirmPassword = !showConfirmPassword"
        >
          <i :class="showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
        </button>
      </div>
      <div
        v-if="confirmPasswordError"
        class="register-form__message"
      >
        <small class="register-form__error">{{ confirmPasswordError }}</small>
      </div>
    </div>

    <ButtonWithSpinner
      type="submit"
      :is-loading="isSubmitting"
      :text="'ZAREJESTRUJ SIĘ'"
      :variant="buttonState"
    />

    <div
      v-if="registerError"
      class="register-form__message register-form__message--center"
    >
      <small class="register-form__error">{{ registerError }}</small>
    </div>

    <div class="register-form__links">
      <button
        type="button"
        class="register-form__link"
        @click="$emit('toggleForm')"
      >
        Masz już konto? Zaloguj się
      </button>
    </div>
  </form>
</template>

<style scoped lang="scss">
@use 'sass:color';

.register-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  padding: 1.5rem;
  background: $white;
  border-radius: 1rem;

  &__group {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
  }

  &__label {
    font-size: 2rem;
    font-weight: 700;
    color: $black-400;
    transition: color 0.2s ease;
  }

  &__input {
    height: 3.5rem;
    padding: 0 1.25rem;
    border: 2px solid $brown-400;
    border-radius: 0.75rem;
    font-size: 1.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: $white;
    color: $black-400;
    width: 100%;

    &::placeholder {
      color: $brown-700;
      font-size: 1.5rem;
      opacity: 0.7;
    }

    &:focus {
      outline: none;
      border-color: $brown-200;
      box-shadow: 0 0 0 4px rgba($brown-200, 0.15);
    }

    &--error {
      border-color: #f33;

      &:focus {
        box-shadow: 0 0 0 4px rgb(255 51 51 / 15%);
      }
    }
  }

  &__message {
    min-height: 0;
    margin-top: 0.25rem;
    display: none;

    &--center {
      text-align: center;
    }

    &:has(.register-form__error:not(:empty)) {
      display: block;
      min-height: 1.25rem;
    }
  }

  &__error {
    font-size: 1.5rem;
    color: #f33;
    display: block;
    font-weight: 500;
  }

  &__info {
    font-size: 1.5rem;
    color: $brown-700;
    display: block;
    margin-top: 0.25rem;
  }

  &__button {
    height: 4.5rem;
    background: $brown-200;
    color: $white;
    border: none;
    border-radius: 0.75rem;
    font-size: 1.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
      background: color.adjust($brown-200, $lightness: -5%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($brown-200, 0.3);
    }

    &:active {
      background: color.adjust($brown-200, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba($brown-200, 0.2);
    }
  }

  &__links {
    display: flex;
    justify-content: center;
    margin-top: 0.5rem;
  }

  &__link {
    font-size: 1.75rem;
    color: $brown-200;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    letter-spacing: 0.3px;
    line-height: 1.4;

    &:hover {
      color: $brown-100;
      background-color: rgba($brown-200, 0.1);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__toggle-password {
    position: absolute;
    right: 1rem;
    background: none;
    border: none;
    color: $brown-300;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;

    &:hover {
      color: $brown-200;
    }

    i {
      font-size: 1.6rem;
    }
  }
}
</style>
